import { z } from 'zod';

// 服务状态枚举
export enum ServiceStatus {
  Running = 'running',
  Stopped = 'stopped',
  Unhealthy = 'unhealthy',
  Unknown = 'unknown',
  Starting = 'starting',
  Stopping = 'stopping',
}

// 服务类型枚举
export enum ServiceType {
  Docker = 'docker',
  DockerCompose = 'docker-compose',
  Node = 'node',
  Static = 'static',
}

// 健康检查配置
export const HealthCheckConfigSchema = z.object({
  enabled: z.boolean().default(true),
  endpoint: z.string().url().optional(),
  interval: z.number().min(1000).default(30000), // 默认30秒
  timeout: z.number().min(1000).default(5000), // 默认5秒
  retries: z.number().min(0).default(3),
});

export type HealthCheckConfig = z.infer<typeof HealthCheckConfigSchema>;

// 服务指标
export const ServiceMetricsSchema = z.object({
  uptime: z.number().default(0), // 运行时间（秒）
  requestCount: z.number().default(0),
  errorCount: z.number().default(0),
  errorRate: z.number().default(0),
  lastResponseTime: z.number().optional(), // 最后响应时间（毫秒）
  avgResponseTime: z.number().default(0),
  healthChecksPassed: z.number().default(0),
  healthChecksFailed: z.number().default(0),
});

export type ServiceMetrics = z.infer<typeof ServiceMetricsSchema>;

// 已部署的服务
export const DeployedServiceSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  serverId: z.string(),
  deploymentId: z.string(),
  name: z.string(),
  type: z.nativeEnum(ServiceType),
  host: z.string(),
  port: z.number().optional(),
  url: z.string().url(),
  healthCheck: HealthCheckConfigSchema.optional(),
  status: z.nativeEnum(ServiceStatus),
  version: z.string(),
  deployedAt: z.string().datetime(),
  lastHealthCheck: z.string().datetime().optional(),
  lastStatusChange: z.string().datetime().optional(),
  metrics: ServiceMetricsSchema.optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  tags: z.array(z.string()).optional(),
});

export type DeployedService = z.infer<typeof DeployedServiceSchema>;

// 服务操作
export enum ServiceOperation {
  Start = 'start',
  Stop = 'stop',
  Restart = 'restart',
  Destroy = 'destroy',
  HealthCheck = 'health-check',
}

// 操作结果
export const OperationResultSchema = z.object({
  serviceId: z.string(),
  operation: z.nativeEnum(ServiceOperation),
  success: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
  timestamp: z.string().datetime(),
  duration: z.number().optional(), // 操作耗时（毫秒）
});

export type OperationResult = z.infer<typeof OperationResultSchema>;

// 服务事件
export enum ServiceEvent {
  Registered = 'registered',
  Started = 'started',
  Stopped = 'stopped',
  Destroyed = 'destroyed',
  HealthCheckPassed = 'health-check-passed',
  HealthCheckFailed = 'health-check-failed',
  StatusChanged = 'status-changed',
  MetricsUpdated = 'metrics-updated',
}

// 事件记录
export const ServiceEventRecordSchema = z.object({
  id: z.string(),
  serviceId: z.string(),
  event: z.nativeEnum(ServiceEvent),
  timestamp: z.string().datetime(),
  details: z.record(z.string(), z.any()).optional(),
});

export type ServiceEventRecord = z.infer<typeof ServiceEventRecordSchema>;

// 注册中心配置
export const RegistryConfigSchema = z.object({
  database: z.object({
    type: z.enum(['sqlite', 'postgres']).default('sqlite'),
    path: z.string().optional(),
    connectionString: z.string().optional(),
  }),
  healthCheck: z.object({
    enabled: z.boolean().default(true),
    defaultInterval: z.number().default(30000),
    defaultTimeout: z.number().default(5000),
    defaultRetries: z.number().default(3),
  }),
  api: z.object({
    enabled: z.boolean().default(true),
    port: z.number().default(9091),
    host: z.string().default('localhost'),
    auth: z.object({
      enabled: z.boolean().default(false),
      token: z.string().optional(),
    }).optional(),
  }),
  monitoring: z.object({
    retentionDays: z.number().default(30),
    metricsInterval: z.number().default(60000), // 1分钟
  }),
});

export type RegistryConfig = z.infer<typeof RegistryConfigSchema>;

// API 响应
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.any().optional(),
  error: z.string().optional(),
  timestamp: z.string().datetime(),
});

export type ApiResponse = z.infer<typeof ApiResponseSchema>;

// 查询过滤器
export const ServiceFilterSchema = z.object({
  status: z.nativeEnum(ServiceStatus).optional(),
  type: z.nativeEnum(ServiceType).optional(),
  projectId: z.string().optional(),
  serverId: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export type ServiceFilter = z.infer<typeof ServiceFilterSchema>;